import { styled } from '@aether/styles'
import { ShopifyImageMediaPayload } from '@aether/models'
import { useEffect, useRef, useState } from 'react'

import {
  GalleryThumbnail,
  THUMBNAIL_WIDTH,
  THUMBNAIL_PADDING_LEFT,
} from './GalleryThumbnail'
import { Button, ratios } from '@aether/components'
import { ArrowDown } from '@aether/icons'

interface GalleryThumbnailsProps {
  images: ShopifyImageMediaPayload[]
  // name needed as each of the thumbnails is related to an input
  // in ourder to group the inputs they need to have a consistent name
  name: string
  activeImageIndex: number
  onImageSelected: (index: number) => void
  priority: boolean
}

export const Wrap = styled('div', {
  position: 'absolute',
  top: 0,
  bottom: 0,
  overflow: 'hidden',
  overflowY: 'scroll',
  width: THUMBNAIL_WIDTH + THUMBNAIL_PADDING_LEFT + 5,
  height: '100%',
  padding: '0 5px 5px 0',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollBehavior: 'smooth',
})
export const SecondWrap = styled('div', {})

export const FieldsetWrapper = styled('fieldset', {
  display: 'grid',
  gridAutoFlow: 'row',
  gap: '$12',
  position: 'relative',
  '&:before': {
    content: '',
    width: '1px',
    height: '100%',
    backgroundColor: '$gray_light_fourth',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
})

export const Overlay = styled(Button, {
  position: 'absolute',
  bottom: 0,
  left: THUMBNAIL_PADDING_LEFT,
  width: THUMBNAIL_WIDTH,
  height: 60,
  background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 58.17%)',
  display: 'grid',
  alignItems: 'end',
  justifyContent: 'center',
  opacity: 1,
  transition: 'opacity 0.2s ease-in-out',
  variants: {
    isVisible: {
      false: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },
})

export const GalleryThumbnails = ({
  images,
  name,
  activeImageIndex,
  onImageSelected,
  priority,
}: GalleryThumbnailsProps) => {
  const lastThumbnailRef = useRef<HTMLDivElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [isLastThumbnailVisible, setIsLastThumbnailVisible] = useState(true)
  const shouldShowOverlay = images.length >= 4

  const handleOverlayClick = () => {
    if (wrapRef.current) {
      const currentScroll = wrapRef.current.scrollTop
      const scrollAmount = (THUMBNAIL_WIDTH * ratios.portrait1 + 12) * 2 // Scroll by 2 thumbnails
      wrapRef.current.scrollTo({
        top: currentScroll + scrollAmount,
        behavior: 'smooth',
      })
    }
  }

  useEffect(() => {
    if (!shouldShowOverlay) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        setIsLastThumbnailVisible(entry.isIntersecting)
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.8,
      },
    )

    if (lastThumbnailRef.current) {
      observer.observe(lastThumbnailRef.current)
    }

    return () => {
      if (lastThumbnailRef.current) {
        observer.unobserve(lastThumbnailRef.current)
      }
    }
  }, [shouldShowOverlay])

  return images.length > 0 ? (
    <div>
      <Wrap ref={wrapRef}>
        <FieldsetWrapper>
          {images.map((image, index) => {
            const isLastThumbnail = index === images.length - 1
            return (
              <div
                key={image.image.src}
                ref={isLastThumbnail ? lastThumbnailRef : undefined}
              >
                <GalleryThumbnail
                  image={image}
                  name={name}
                  checked={index === activeImageIndex}
                  index={index}
                  onSelected={onImageSelected}
                  priority={priority && index === 0}
                />
              </div>
            )
          })}
        </FieldsetWrapper>
      </Wrap>
      <Overlay
        tabIndex={-1}
        appearance="grid"
        onClick={handleOverlayClick}
        isVisible={shouldShowOverlay && !isLastThumbnailVisible}
      >
        <ArrowDown />
      </Overlay>
    </div>
  ) : null
}
